// A customer's lookups from lookup_trail (ENG2-2849): unique companies and daily
// lookups, with and without financial data, by month, country, channel and
// endpoint. CustomTabs only mounts the open tab, so the count runs when it is
// opened and not with the customer page.

import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBuilding,
  faCalendarDay,
  faChartLine,
  faMinus,
} from '@fortawesome/free-solid-svg-icons'
import { StatCard } from 'components/ui'
import {
  LookupUsageMetrics,
  LookupUsageResponse,
} from 'services/cm-api/get-lookup-usage'
import { useLookupUsage } from 'services/queries/use-lookup-usage'
import { tokens } from 'styles/tokens'
import { formatDate, formatNumber } from 'pages/nordic-lookups-usage/helpers'
import {
  channelLabel,
  isTooMuchData,
  monthLabel,
  monthOptions,
  TRAIL_STARTED_AT,
  yearsSinceTrailStart,
} from './helpers'

const ALL_MONTHS = 'all'

const headSx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
  lineHeight: 1.3,
  verticalAlign: 'bottom',
}

// Checked with the palette validator on both surfaces; amber needs a darker step
// on the dark one. The numbers sit next to the bar, so nothing rests on colour.
const useBarColours = () => {
  const { palette } = useTheme()
  return {
    withFinancial: tokens.colors.info[500],
    withoutFinancial:
      palette.mode === 'dark'
        ? tokens.colors.primary[800]
        : tokens.colors.primary[700],
  }
}

const Section: React.FC<{ title: string; caption?: string }> = ({
  title,
  caption,
  children,
}) => (
  <Box sx={{ mt: 4 }}>
    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
      {title}
    </Typography>
    {caption && (
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
        {caption}
      </Typography>
    )}
    <Box sx={{ mt: 1.5 }}>{children}</Box>
  </Box>
)

const TotalsRow: React.FC<{
  title: string
  prefix: string
  icon: typeof faBuilding
  total: number
  withFinancial: number
  withoutFinancial: number
}> = ({ title, prefix, icon, total, withFinancial, withoutFinancial }) => (
  <Box
    data-testid={`lookup-usage-total-${title}`}
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
    }}
  >
    <StatCard
      label={title}
      value={formatNumber(total)}
      icon={<FontAwesomeIcon icon={icon} />}
      iconVariant="green"
    />
    <StatCard
      label={`${prefix} with financial data`}
      value={formatNumber(withFinancial)}
      icon={<FontAwesomeIcon icon={faChartLine} />}
      iconVariant="blue"
    />
    <StatCard
      label={`${prefix} without financial data`}
      value={formatNumber(withoutFinancial)}
      icon={<FontAwesomeIcon icon={faMinus} />}
      iconVariant="orange"
    />
  </Box>
)

const Legend: React.FC = () => {
  const colours = useBarColours()
  const items = [
    { label: 'With financial data', colour: colours.withFinancial },
    { label: 'Without financial data', colour: colours.withoutFinancial },
  ]
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
      {items.map(({ label, colour }) => (
        <Box
          key={label}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <Box
            sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: colour }}
          />
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

// Daily lookups of one month, its length relative to the busiest month.
const MonthBar: React.FC<{ metrics: LookupUsageMetrics; busiest: number }> = ({
  metrics,
  busiest,
}) => {
  const colours = useBarColours()
  const segments = [
    {
      label: 'With financial data',
      value: metrics.daily_with_financial,
      colour: colours.withFinancial,
    },
    {
      label: 'Without financial data',
      value: metrics.daily_without_financial,
      colour: colours.withoutFinancial,
    },
  ].filter(({ value }) => value > 0)
  const width = busiest > 0 ? (metrics.daily_lookups / busiest) * 100 : 0

  return (
    <Box sx={{ minWidth: 120 }}>
      <Box sx={{ display: 'flex', gap: '2px', width: `${width}%` }}>
        {segments.map(({ label, value, colour }) => (
          <Tooltip key={label} title={`${label}: ${formatNumber(value)}`} arrow>
            <Box
              sx={{
                flexGrow: value,
                flexBasis: 0,
                minWidth: 2,
                height: 12,
                bgcolor: colour,
                '&:first-of-type': { borderRadius: '4px 0 0 4px' },
                '&:last-of-type': { borderRadius: '0 4px 4px 0' },
                '&:only-of-type': { borderRadius: '4px' },
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Box>
  )
}

type MetricsRow = LookupUsageMetrics & { key: string; label: string }

const MetricsTable: React.FC<{
  name: string
  firstColumn: string
  rows: MetricsRow[]
  showBar?: boolean
}> = ({ name, firstColumn, rows, showBar = false }) => {
  const busiest = Math.max(0, ...rows.map((row) => row.daily_lookups))
  return (
    <TableContainer>
      <Table size="small" data-testid={`lookup-usage-table-${name}`}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>{firstColumn}</TableCell>
            <TableCell sx={headSx} align="right">
              Unique companies
            </TableCell>
            <TableCell sx={headSx} align="right">
              Unique with financial
            </TableCell>
            <TableCell sx={headSx} align="right">
              Unique without financial
            </TableCell>
            <TableCell sx={headSx} align="right">
              Daily lookups
            </TableCell>
            <TableCell sx={headSx} align="right">
              Daily with financial
            </TableCell>
            <TableCell sx={headSx} align="right">
              Daily without financial
            </TableCell>
            {showBar && <TableCell sx={headSx}>Daily lookups split</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.label}</TableCell>
              <TableCell align="right">
                {formatNumber(row.unique_companies)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.unique_with_financial)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.unique_without_financial)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.daily_lookups)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.daily_with_financial)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.daily_without_financial)}
              </TableCell>
              {showBar && (
                <TableCell>
                  <MonthBar metrics={row} busiest={busiest} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const EndpointsTable: React.FC<{
  endpoints: LookupUsageResponse['endpoints']
}> = ({ endpoints }) => (
  <TableContainer>
    <Table size="small" data-testid="lookup-usage-table-endpoints">
      <TableHead>
        <TableRow>
          <TableCell sx={headSx}>Endpoint</TableCell>
          <TableCell sx={headSx} align="right">
            Unique companies
          </TableCell>
          <TableCell sx={headSx} align="right">
            Daily lookups
          </TableCell>
          <TableCell sx={headSx}>In the totals</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {endpoints.map((endpoint) => (
          <TableRow key={endpoint.endpoint}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {endpoint.endpoint}
            </TableCell>
            <TableCell align="right">
              {formatNumber(endpoint.unique_companies)}
            </TableCell>
            <TableCell align="right">
              {formatNumber(endpoint.daily_lookups)}
            </TableCell>
            <TableCell>
              {endpoint.counted ? (
                endpoint.financial && (
                  <Chip label="Financial" size="small" color="info" />
                )
              ) : (
                <Chip label="Not counted" size="small" variant="outlined" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const Usage: React.FC<{ usage: LookupUsageResponse }> = ({ usage }) => {
  const { totals, months, countries, channels, endpoints } = usage
  if (!months.length && !endpoints.length) {
    return (
      <Alert severity="info" sx={{ mt: 3 }} data-testid="lookup-usage-empty">
        No lookups in this period.
      </Alert>
    )
  }
  return (
    <>
      <Box sx={{ display: 'grid', gap: 2, mt: 3 }}>
        <TotalsRow
          title="Unique companies"
          prefix="Unique"
          icon={faBuilding}
          total={totals.unique_companies}
          withFinancial={totals.unique_with_financial}
          withoutFinancial={totals.unique_without_financial}
        />
        <TotalsRow
          title="Daily lookups"
          prefix="Daily"
          icon={faCalendarDay}
          total={totals.daily_lookups}
          withFinancial={totals.daily_with_financial}
          withoutFinancial={totals.daily_without_financial}
        />
      </Box>
      {usage.month === null && (
        <Section
          title="By month"
          caption="Daily lookups add up to the year. Unique companies do not: a company looked up in two months counts once in each."
        >
          <Legend />
          <MetricsTable
            name="months"
            firstColumn="Month"
            showBar
            rows={months.map((row) => ({
              ...row,
              key: row.month,
              label: monthLabel(row.month),
            }))}
          />
        </Section>
      )}
      <Section title="By country">
        <MetricsTable
          name="countries"
          firstColumn="Country"
          rows={countries.map((row) => ({
            ...row,
            key: row.country,
            label: row.country,
          }))}
        />
      </Section>
      <Section
        title="By channel"
        caption="Does not add up to the totals: a company looked up through two channels counts in both."
      >
        <MetricsTable
          name="channels"
          firstColumn="Channel"
          rows={channels.map((row) => ({
            ...row,
            key: row.channel,
            label: channelLabel(row.channel),
          }))}
        />
      </Section>
      <Section
        title="By endpoint"
        caption="Does not add up to the totals: one lookup usually calls several endpoints. Endpoints marked Not counted are not part of any total."
      >
        <EndpointsTable endpoints={endpoints} />
      </Section>
    </>
  )
}

const PageNote: React.FC<{ first: string | null }> = ({ first }) => (
  <Typography
    data-testid="lookup-usage-note"
    sx={{ fontSize: 13, color: 'text.secondary', mt: 4, maxWidth: 900 }}
  >
    A lookup is one company, by one account, on one day (UTC). With financial
    data means at least one of the financial endpoints was called. Counted from
    the lookup trail, which began on {formatDate(TRAIL_STARTED_AT)} and keeps 13
    whole months
    {first
      ? `; this customer's first lookup on it is ${formatDate(first)}`
      : ''}
    . The trail is best-effort and leaves out searches, exports, companies
    nested in another response, failed requests and traffic on the older /v1/
    API versions, so a customer on those versions is undercounted.
  </Typography>
)

type Bounds = Pick<
  LookupUsageResponse,
  'years' | 'first_lookup_at' | 'last_lookup_at'
>

const LookupUsage: React.FC<{ customerId: number | string }> = ({
  customerId,
}) => {
  const [year, setYear] = useState<number | undefined>()
  const [month, setMonth] = useState<number | undefined>()
  // The last answer's bounds keep the selectors filled while a period loads or
  // turns out too large to count.
  const [bounds, setBounds] = useState<Bounds | undefined>()

  const {
    data: usage,
    error,
    isError,
    isLoading,
    isIdle,
    refetch,
  } = useLookupUsage(customerId, { year, month })

  useEffect(() => {
    if (usage) setBounds(usage)
  }, [usage])

  const fallbackYears = yearsSinceTrailStart()
  const years = bounds?.years.length ? bounds.years : fallbackYears
  const shownYear = year ?? usage?.year ?? years[0]
  const months = monthOptions(
    shownYear,
    bounds?.first_lookup_at ?? null,
    bounds?.last_lookup_at ?? null,
  )
  const hasNoLookups = bounds !== undefined && bounds.years.length === 0

  return (
    <Box sx={{ py: 3 }} data-testid="lookup-usage">
      {!hasNoLookups && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            size="small"
            label="Year"
            value={String(shownYear)}
            sx={{ minWidth: 120 }}
            inputProps={{ 'data-testid': 'lookup-usage-year' }}
            onChange={(event) => {
              setYear(Number(event.target.value))
              setMonth(undefined)
            }}
          >
            {years.map((option) => (
              <MenuItem key={option} value={String(option)}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Month"
            value={month === undefined ? ALL_MONTHS : String(month)}
            sx={{ minWidth: 160 }}
            inputProps={{ 'data-testid': 'lookup-usage-month' }}
            onChange={(event) => {
              const { value } = event.target
              setYear(shownYear)
              setMonth(value === ALL_MONTHS ? undefined : Number(value))
            }}
          >
            <MenuItem value={ALL_MONTHS}>Whole year</MenuItem>
            {months.map(({ value, label }) => (
              <MenuItem key={value} value={String(value)}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {(isLoading || isIdle) && (
        <Box
          data-testid="lookup-usage-loading"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3 }}
        >
          <CircularProgress size={22} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Counting lookups. A large customer can take up to 45 seconds.
          </Typography>
        </Box>
      )}

      {isError && isTooMuchData(error) && (
        <Alert
          severity="info"
          sx={{ mt: 3 }}
          data-testid="lookup-usage-too-much"
        >
          {month === undefined
            ? `${shownYear} holds too much data to count at once. Pick a month above.`
            : 'This month holds too much data to count within 45 seconds. Try again later.'}
        </Alert>
      )}

      {isError && !isTooMuchData(error) && (
        <Alert
          severity="error"
          sx={{ mt: 3 }}
          data-testid="lookup-usage-error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Try again
            </Button>
          }
        >
          Could not load the lookups for this customer.
        </Alert>
      )}

      {usage && hasNoLookups && (
        <Alert severity="info" data-testid="lookup-usage-none">
          This customer has no lookups on the trail yet.
        </Alert>
      )}

      {usage && !hasNoLookups && <Usage usage={usage} />}

      <PageNote first={bounds?.first_lookup_at ?? null} />
    </Box>
  )
}

export default LookupUsage
